import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Users, DollarSign, ShoppingCart, Eye, BarChart3, PieChart, Activity } from 'lucide-react';

const Analytics = () => {
  const [timeRange, setTimeRange] = useState('7d');

  const metrics = [
    {
      name: 'Total Revenue',
      value: '$45,231',
      change: '+23%',
      changeType: 'positive',
      icon: DollarSign,
      color: 'bg-green-500'
    },
    {
      name: 'Total Orders',
      value: '2,847',
      change: '+12%',
      changeType: 'positive',
      icon: ShoppingCart,
      color: 'bg-blue-500'
    },
    {
      name: 'Total Users',
      value: '12,847',
      change: '+8%',
      changeType: 'positive',
      icon: Users,
      color: 'bg-purple-500'
    },
    {
      name: 'Conversion Rate',
      value: '3.24%',
      change: '-1.2%',
      changeType: 'negative',
      icon: TrendingUp,
      color: 'bg-orange-500'
    }
  ];



  const trafficSources = [
    { source: 'Direct', percentage: 45, color: 'bg-blue-500' },
    { source: 'Organic Search', percentage: 30, color: 'bg-green-500' },
    { source: 'Social Media', percentage: 15, color: 'bg-purple-500' },
    { source: 'Referral', percentage: 10, color: 'bg-orange-500' }
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600">Track your business performance and insights.</p>
        </div>
        
        {/* Time Range Selector */}
        <div className="flex items-center space-x-2 mt-4 sm:mt-0">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="block px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric) => (
          <div key={metric.name} className="card">
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${metric.color}`}>
                <metric.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{metric.name}</p>
                <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
              </div>
            </div>
            <div className="mt-4 flex items-center">
              {metric.changeType === 'positive' ? (
                <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
              )}
              <span className={`text-sm font-medium ${
                metric.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
              }`}>
                {metric.change}
              </span>
              <span className="text-sm text-gray-600 ml-2">from last period</span>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue Trend</h3>
          <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">Revenue chart would go here</p>
              <p className="text-sm text-gray-400">Integrate with Chart.js or Recharts</p>
            </div>
          </div>
        </div>

        {/* Traffic Sources Chart */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Traffic Sources</h3>
          <div className="space-y-4">
            {trafficSources.map((source) => (
              <div key={source.source} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded-full ${source.color}`}></div>
                  <span className="text-sm font-medium text-gray-900">{source.source}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${source.color}`}
                      style={{ width: `${source.percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm text-gray-600 w-12 text-right">{source.percentage}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* User Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">


        {/* User Activity */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent User Activity</h3>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <div className="flex-1">
                <p className="text-sm text-gray-900">New user registration</p>
                <p className="text-xs text-gray-500">2 minutes ago</p>
              </div>
              <span className="text-sm text-green-600">+1</span>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <div className="flex-1">
                <p className="text-sm text-gray-900">Order completed</p>
                <p className="text-xs text-gray-500">5 minutes ago</p>
              </div>
              <span className="text-sm text-blue-600">$99.99</span>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              <div className="flex-1">
                <p className="text-sm text-gray-900">Survey response submitted</p>
                <p className="text-xs text-gray-500">10 minutes ago</p>
              </div>
              <span className="text-sm text-purple-600">5★</span>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <div className="flex-1">
                <p className="text-sm text-gray-900">Survey incomplete</p>
                <p className="text-xs text-gray-500">15 minutes ago</p>
              </div>
              <span className="text-sm text-orange-600">-1</span>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Analytics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Conversion Funnel */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Conversion Funnel</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Page Views</span>
              <span className="text-sm font-medium text-gray-900">12,847</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Add to Cart</span>
              <span className="text-sm font-medium text-gray-900">3,421</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Checkout</span>
              <span className="text-sm font-medium text-gray-900">1,234</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Purchase</span>
              <span className="text-sm font-medium text-gray-900">892</span>
            </div>
          </div>
        </div>

        {/* Geographic Distribution */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Top Locations</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">United States</span>
              <span className="text-sm font-medium text-gray-900">45%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">United Kingdom</span>
              <span className="text-sm font-medium text-gray-900">18%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Canada</span>
              <span className="text-sm font-medium text-gray-900">12%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Germany</span>
              <span className="text-sm font-medium text-gray-900">8%</span>
            </div>
          </div>
        </div>

        {/* Device Usage */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Device Usage</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Desktop</span>
              <span className="text-sm font-medium text-gray-900">58%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Mobile</span>
              <span className="text-sm font-medium text-gray-900">35%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Tablet</span>
              <span className="text-sm font-medium text-gray-900">7%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
